// src/components/Pitch.js
import React, { useRef, useState } from "react";
import "./Pitch.css"; // optional styling

const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_THRESHOLD = 10;

export default function Pitch({
  team = {},
  playersById = new Map(),
  positions = [],
  captainID,
  onPlayerClick,
  pointsById = {},
  chipsUsed = {},
  currentGameweek,
  valueSuffix = "m",
  dreamTeamSet = null,
  availablePlayerIDs = null,
  newPlayerIDs = null, // Set of IDs added since the last save (Pick Team)
  // Long-press quick menu (filled tiles only) — wired through to whatever
  // logic the parent already has; Pitch doesn't implement any of this itself.
  onMakeCaptain,
  onAddEditPlayer,
  onRemovePlayer,
  isLocked = false,
}) {
  // ── Long-press state ──────────────────────────────────────────────────────
  const [quickMenu, setQuickMenu] = useState(null); // { playerID, pos, top, left } | null
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchTargetRef = useRef(null);

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleTileTouchStart = (playerID, pos, e) => {
    if (isLocked) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    touchTargetRef.current = e.currentTarget;
    longPressFiredRef.current = false;
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      const rect = touchTargetRef.current?.getBoundingClientRect?.();
      setQuickMenu({
        playerID,
        pos,
        top: rect ? rect.bottom + 6 : touchStartRef.current.y,
        left: rect ? rect.left + rect.width / 2 : touchStartRef.current.x,
      });
    }, LONG_PRESS_MS);
  };

  const handleTileTouchMove = (e) => {
    if (!longPressTimerRef.current) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    if (Math.sqrt(dx * dx + dy * dy) > LONG_PRESS_MOVE_THRESHOLD) {
      clearLongPressTimer();
    }
  };

  const handleTileTouchEnd = () => {
    clearLongPressTimer();
  };

  const handleTileTouchCancel = () => {
    clearLongPressTimer();
    longPressFiredRef.current = false;
  };

  const handleTileClick = (playerID, pos) => {
    // Swallow the tap that follows a long-press-triggered menu open, so a
    // normal tap still opens the player card exactly as before otherwise.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    onPlayerClick?.(playerID, pos);
  };

  const closeQuickMenu = () => setQuickMenu(null);

  return (
    <div className="pitch">
                        {chipsUsed && chipsUsed[currentGameweek] && chipsUsed[currentGameweek] !== "None" && (
                    <div className="chip-badge-top">{chipsUsed[currentGameweek]}</div>
                  )}
      <div className="pitch-lines">
        <div className="penalty-box top">
          <div className="six-yard-box">
            <div className="goal-box"></div>
          </div>
        </div>
        <div className="penalty-box bottom">
          <div className="six-yard-box">
            <div className="goal-box"></div>
          </div>
        </div>
      </div>

      {positions.map((pos) => {
        let rowClass = "";
        if (pos === "DEF") rowClass = "pitch-row def";
        else if (pos === "MID") rowClass = "pitch-row mid";
        else if (pos === "FWD") rowClass = "pitch-row fwd";
        else rowClass = "pitch-row";

        return (
          <div key={pos} className={rowClass}>
            {(team[pos] || []).map((playerID) => {
              const p = playersById.get(playerID);
              if (!p) return null;
              const isCaptain = playerID === captainID;
              const rawPoints = pointsById[playerID];
              let valueToShow;
              if (rawPoints !== undefined && rawPoints !== null) {
                // If it's an object like { total: X }
                if (typeof rawPoints === "object" && rawPoints.total !== undefined) {
                  valueToShow = rawPoints.total;
                } else {
                  valueToShow = rawPoints;
                }
              } else {
                valueToShow = p.displayValue;
              }
              // Background tint based on points value (only when pointsById is provided)
              let tileBg;
              if (rawPoints !== undefined && rawPoints !== null) {
                const ptVal = typeof rawPoints === "object" ? rawPoints.total : rawPoints;
                if (ptVal > 3) tileBg = "rgba(34, 197, 94, 0.35)";
                else if (ptVal < 2) tileBg = "rgba(239, 68, 68, 0.35)";
              }

              const isDreamTeam = dreamTeamSet && dreamTeamSet.has(playerID);
              const isAvailable = availablePlayerIDs && availablePlayerIDs.has(playerID);
              return (
                <div
                  key={playerID}
                  className={`player-tile ${isCaptain ? "captain" : ""}`}
                  style={{
                    ...(tileBg ? { background: tileBg } : {}),
                    position: "relative",
                    ...(isAvailable ? { outline: "2.5px solid #4CAF50", outlineOffset: "2px" } : {}),
                  }}
                  onClick={() => handleTileClick(playerID, pos)}
                  onTouchStart={(e) => handleTileTouchStart(playerID, pos, e)}
                  onTouchMove={handleTileTouchMove}
                  onTouchEnd={handleTileTouchEnd}
                  onTouchCancel={handleTileTouchCancel}
                >
                  {newPlayerIDs && newPlayerIDs.has(playerID) && (
                    <span className="player-tile-new" aria-label="New, not saved yet">NEW</span>
                  )}
                  {isDreamTeam && (
                    <span style={{ position: "absolute", top: "3px", right: "4px", fontSize: "10px", lineHeight: 1 }} aria-label="Dream Team">⭐</span>
                  )}
                  {isAvailable && (
                    <span style={{
                      position: "absolute",
                      top: "3px",
                      left: "4px",
                      width: "7px",
                      height: "7px",
                      borderRadius: "50%",
                      background: "#4CAF50",
                      display: "inline-block",
                    }} aria-label="Available" />
                  )}
                  <div className="player-line1">{p.name}{isCaptain ? " (C)" : ""}</div>
                  <div className="player-line2">{p.position}</div>
                  {valueToShow !== undefined && (
                    <div className="player-line3">{`${valueToShow}${valueSuffix}`}</div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {quickMenu && (
        <>
          <div className="pitch-quickmenu-backdrop" onClick={closeQuickMenu} onTouchStart={closeQuickMenu} />
          <div
            className="pitch-quickmenu"
            style={{ top: quickMenu.top, left: quickMenu.left }}
          >
            <button
              type="button"
              onClick={() => {
                onMakeCaptain?.(quickMenu.playerID);
                closeQuickMenu();
              }}
            >
              Make Captain
            </button>
            <button
              type="button"
              onClick={() => {
                onAddEditPlayer?.(quickMenu.playerID, quickMenu.pos);
                closeQuickMenu();
              }}
            >
              Add / Edit
            </button>
            <button
              type="button"
              className="pitch-quickmenu-remove"
              onClick={() => {
                onRemovePlayer?.(quickMenu.playerID, quickMenu.pos);
                closeQuickMenu();
              }}
            >
              Remove
            </button>
          </div>
        </>
      )}
    </div>
  );
}